// The client's money page. Plain language, two ledgers kept visibly apart:
// what the office spreadsheet says, and what happened online. A pending
// payment reads as processing, never as failure.

import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { ClientPortalPayment } from "@duralex/shared";
import {
  PAYMENT_UNAVAILABLE,
  SESSION_EXPIRED,
  downloadClientInvoicePdf,
  downloadClientReceiptPdf,
  fetchClientBilling,
  getClientToken,
  startInvoiceCheckout,
} from "../../../api/clientPortal";
import { PortalHeader } from "../../../components/portal/PortalHeader";
import { usePortalSignOut, useExpiredSession } from "../../../components/portal/portalSession";
import { formatPortalDate as formatDate } from "../../../components/portal/portalDate";
import { fmtCents, fmtDollars } from "../../../lib/money";

// The note under the bills and a checkout refused for the module share one
// caption: the client is never told which switch is off.
const NOT_AVAILABLE_COPY =
  "Online payment is not available right now. Please call our office to pay.";

const PAYMENT_LABEL: Record<string, string> = {
  pending: "Processing",
  succeeded: "Paid",
  expired: "Checkout expired - not charged",
  refunded: "Refunded",
  failed: "Did not go through",
};

// "other" has no useful word - "(payment)" told the client nothing.
const METHOD_WORD: Record<string, string> = {
  cash: "cash",
  check: "check",
  zelle: "Zelle",
};

// Manual money is described by how it arrived; everything else falls
// back to the plain status word.
function outcomeOf(p: ClientPortalPayment): string {
  if (p.provider === "manual") {
    if (p.status === "succeeded") {
      const word = p.method ? METHOD_WORD[p.method] : undefined;
      return word ? `Received by our office (${word})` : "Received by our office";
    }
    if (p.status === "refunded") return "Reversed by our office";
  }
  return PAYMENT_LABEL[p.status] ?? p.status;
}

export default function PortalPayments() {
  const token = getClientToken();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const signOut = usePortalSignOut();
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);

  const returned = params.get("status");
  const backFromCheckout = returned === "success";

  const { data, error, isLoading } = useQuery({
    queryKey: ["portal", "billing", token],
    queryFn: () => fetchClientBilling(backFromCheckout),
    retry: false,
  });
  const expired = useExpiredSession(error);

  async function pay(invoiceId: string) {
    setPayError(null);
    setPayingId(invoiceId);
    try {
      const url = await startInvoiceCheckout(invoiceId);
      window.location.assign(url);
    } catch (err) {
      setPayingId(null);
      const message = err instanceof Error ? err.message : "";
      // The session died mid-click (revoked access, aged-out token): the
      // machine sentinel must never reach the client - route them home.
      if (message === SESSION_EXPIRED) {
        navigate("/portal/login", { replace: true });
        return;
      }
      setPayError(
        message === PAYMENT_UNAVAILABLE
          ? NOT_AVAILABLE_COPY
          : message || "Could not start the payment.",
      );
    }
  }

  async function savePdf(inv: { id: string; number: string | null }) {
    setPayError(null);
    try {
      await downloadClientInvoicePdf(inv.id, inv.number);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message === SESSION_EXPIRED) {
        navigate("/portal/login", { replace: true });
        return;
      }
      setPayError(message || "Could not download the invoice.");
    }
  }

  async function saveReceipt(p: { id: string; number: string | null }) {
    setPayError(null);
    try {
      await downloadClientReceiptPdf(p.id, p.number);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message === SESSION_EXPIRED) {
        navigate("/portal/login", { replace: true });
        return;
      }
      setPayError(message || "Could not download the receipt.");
    }
  }

  if (isLoading || expired) {
    return (
      <main className="portal">
        <PortalHeader />
        <div className="portal-body">
          <div className="portal-card portal-loading">Loading your payments...</div>
        </div>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main className="portal">
        <PortalHeader />
        <div className="portal-body">
          <section className="portal-card">
            <p className="portal-note">
              We could not load your payments right now. Please try again, or call our office.
            </p>
            <button type="button" className="portal-link-btn" onClick={signOut}>
              Sign in again
            </button>
          </section>
        </div>
      </main>
    );
  }

  const open = data.invoices.filter((i) => i.status === "open");
  const balance = data.balanceOnFile;
  const syncedAt = formatDate(balance?.syncedAt ?? null);

  return (
    <main className="portal">
      <PortalHeader />
      <div className="portal-body">
        {backFromCheckout && (
          <div className="portal-ok">
            Thank you - your payment went through. It may take a moment to appear below.
          </div>
        )}
        {returned === "cancelled" && (
          <div className="portal-note">
            Your payment was not completed. You can try again any time.
          </div>
        )}

        {balance && (
          <section className="portal-card">
            <h2>Your balance on file</h2>
            <dl className="portal-facts">
              {balance.totalDollars != null && (
                <div>
                  <dt>Total fee</dt>
                  <dd className="tnum">{fmtDollars(balance.totalDollars)}</dd>
                </div>
              )}
              {balance.paidDollars != null && (
                <div>
                  <dt>Paid so far</dt>
                  <dd className="tnum">{fmtDollars(balance.paidDollars)}</dd>
                </div>
              )}
              {balance.balanceDollars != null && (
                <div>
                  <dt>Balance</dt>
                  <dd className="tnum">{fmtDollars(balance.balanceDollars)}</dd>
                </div>
              )}
            </dl>
            <p className="portal-note">
              From the office records{syncedAt ? `, updated ${syncedAt}` : ""}. Payments listed
              below - online or taken at our office - may not be counted in these totals yet.
            </p>
          </section>
        )}

        {data.plan && (
          <section className="portal-card">
            <h2>Your payment plan</h2>
            <p className="portal-note">
              {fmtCents(data.plan.totalCents)} in {data.plan.installmentCount} monthly parts,{" "}
              {data.plan.paidCount} paid so far.
              {data.plan.nextDueDate && data.plan.nextAmountCents != null
                ? ` Next: ${fmtCents(data.plan.nextAmountCents)} due ${formatDate(data.plan.nextDueDate)}. Each part appears under Bills to pay about a week ahead.`
                : " Every part is settled - thank you."}
            </p>
          </section>
        )}

        <section className="portal-card">
          <h2>Bills to pay</h2>
          {/* A sandbox key charges nothing, and from this seat the button
              and the Stripe receipt look exactly like the real thing. The
              alternative to saying so is letting a client believe a bill
              is settled. */}
          {data.testMode && data.available && open.length > 0 && (
            <p className="portal-warn" role="status">
              Online payment is still being set up. A payment made here is a test - no money moves
              and your bill stays open. Please call our office to pay.
            </p>
          )}
          {payError && (
            <div className="portal-bad" role="alert">
              {payError}
            </div>
          )}
          {open.length === 0 ? (
            <p className="portal-note">You have no bills to pay online right now.</p>
          ) : (
            <ul className="portal-list">
              {open.map((inv) => (
                <li key={inv.id}>
                  <div>
                    <div className="portal-doc-name portal-amount">{fmtCents(inv.amountCents)}</div>
                    <div className="portal-doc-meta">
                      {inv.number ? <span className="mono">{inv.number}</span> : "Invoice"}
                      {inv.description ? ` - ${inv.description}` : ""}
                      {inv.dueDate ? ` - due ${formatDate(inv.dueDate)}` : ""}
                    </div>
                  </div>
                  <div className="portal-row-actions">
                    <button
                      type="button"
                      className="portal-link-btn"
                      onClick={() => void savePdf(inv)}
                    >
                      Invoice PDF
                    </button>
                    {data.available ? (
                      <button
                        type="button"
                        className="portal-cta"
                        disabled={payingId === inv.id}
                        onClick={() => pay(inv.id)}
                      >
                        {payingId === inv.id ? "Opening..." : "Pay now"}
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!data.available && open.length > 0 && (
            <p className="portal-note">{NOT_AVAILABLE_COPY}</p>
          )}
        </section>

        <section className="portal-card">
          <h2>Payment history</h2>
          {data.payments.length === 0 ? (
            <p className="portal-note">
              Nothing here yet. Payments you make online, and payments our office records for you,
              will appear here.
            </p>
          ) : (
            <ul className="portal-list">
              {data.payments.map((p) => (
                <li key={p.id}>
                  <div>
                    {/* Only money the office is holding gets the big figure.
                        An abandoned checkout printed the same way put three
                        $750 lines against one $750 bill. */}
                    {p.status === "succeeded" && p.livemode !== false && (
                      <div className="portal-doc-name portal-amount">{fmtCents(p.amountCents)}</div>
                    )}
                    <div className="portal-doc-meta">
                      {p.status === "succeeded" ? (
                        <>
                          {p.invoiceNumber ? (
                            <span className="mono">{p.invoiceNumber}</span>
                          ) : (
                            "Invoice"
                          )}{" "}
                          - {outcomeOf(p)}
                          {formatDate(p.paidAt) ? ` ${formatDate(p.paidAt)}` : ""}
                          {/* The number on Stripe's emailed receipt, so the
                              client can match that email to this row. The
                              provider gate keeps the label honest if a
                              second processor ever lands. */}
                          {p.provider === "stripe" && p.providerInvoiceNumber ? (
                            <>
                              {" "}
                              - Stripe <span className="mono">{p.providerInvoiceNumber}</span>
                            </>
                          ) : (
                            ""
                          )}
                        </>
                      ) : (
                        <>
                          {outcomeOf(p)} - {fmtCents(p.amountCents)} on{" "}
                          {p.invoiceNumber ? (
                            <span className="mono">{p.invoiceNumber}</span>
                          ) : (
                            "an invoice"
                          )}
                          {formatDate(p.paidAt) ? ` ${formatDate(p.paidAt)}` : ""}
                          {p.status === "refunded" ? " - this bill is due again" : ""}
                        </>
                      )}
                      {/* Only false says "test"; money taken by hand and
                          rows older than the stamp say nothing. */}
                      {p.livemode === false ? " - test payment, no money moved" : ""}
                    </div>
                  </div>
                  <div className="portal-row-actions">
                    {p.status === "succeeded" && (
                      <button
                        type="button"
                        className="portal-link-btn"
                        onClick={() => void savePdf({ id: p.invoiceId, number: p.invoiceNumber })}
                      >
                        Invoice PDF
                      </button>
                    )}
                    {p.receiptUrl ? (
                      <a
                        className="portal-link-btn"
                        href={p.receiptUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Receipt
                      </a>
                    ) : p.status === "succeeded" ? (
                      // Money the office took by hand has no processor page
                      // to link - our own receipt stands in for it.
                      <button
                        type="button"
                        className="portal-link-btn"
                        onClick={() => void saveReceipt({ id: p.id, number: p.invoiceNumber })}
                      >
                        Receipt
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
